import { Box, Flex, Input, Field } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InvoiceFormData } from "@/types";
import { PreFilledBadges } from "../../invoiceAttachmentSection/PreFilledInformation";
import { useEffect } from "react";
import { Heading, ErrorMessage } from "@digital-services-gard-as/anchor-react";

export const InvoiceDetails = () => {
    const { t } = useTranslation(["invoices", "errorMessages"]);
    const {
        register,
        trigger,
        watch,
        formState: { errors, touchedFields, isSubmitted }
    } = useFormContext<InvoiceFormData>();

    const invoiceDate = watch("invoiceDate");
    const invoiceDueDate = watch("invoiceDueDate");
    const invoiceNumber = watch("invoiceNumber");

    useEffect(() => {
        trigger("invoiceDueDate");
    }, [invoiceDueDate, trigger]);

    const fieldValues = { invoiceDate, invoiceDueDate, invoiceNumber };

    // A field that is still empty because OCR found nothing should stay neutral
    // until the user has been in it or tried to submit.
    const errorMessage = (field: keyof typeof fieldValues): string | undefined => {
        const isReported = isSubmitted || Boolean(touchedFields[field]) || Boolean(fieldValues[field]);
        return isReported ? errors[field]?.message : undefined;
    };

    const invoiceDateError = errorMessage("invoiceDate");
    const invoiceDueDateError = errorMessage("invoiceDueDate");
    const invoiceNumberError = errorMessage("invoiceNumber");

    return (
        <Box bg="white" p={{ base: "1rem", md: "1.5rem" }} alignContent="flex-start" borderRadius="md" boxShadow="xs">
            <Heading size="md" style={{ marginBottom: "0.5rem" }}>
                {t("invoices:createInvoice.feesAndExpenses.invoiceDetails")}
            </Heading>
            <Flex gap={6} align="flex-start" maxH="6rem" mb={4}>
                <Field.Root flex="1" minW="260px" maxW="320px" w="100%" invalid={Boolean(invoiceDateError)}>
                    <Flex gap="0.5rem" alignItems="center">
                        <Field.Label fontWeight="anchor.bold">
                            {t("invoices:createInvoice.feesAndExpenses.invoiceDate")}
                        </Field.Label>
                        <PreFilledBadges fieldName="invoiceDate" />
                    </Flex>

                    <Input
                        id="invoiceDate"
                        type="date"
                        autoComplete="off"
                        data-lpignore="true"
                        {...register("invoiceDate")}
                    />

                    <ErrorMessage size="sm" data-testid="invoiceDateError">
                        {invoiceDateError}
                    </ErrorMessage>
                </Field.Root>

                <Field.Root flex="1" minW="260px" maxW="320px" w="100%" invalid={Boolean(invoiceDueDateError)}>
                    <Flex gap="0.5rem" alignItems="center">
                        <Field.Label fontWeight="anchor.bold">
                            {t("invoices:createInvoice.feesAndExpenses.invoiceDueDate")}
                        </Field.Label>
                        <PreFilledBadges fieldName="invoiceDueDate" />
                    </Flex>

                    <Input
                        id="invoiceDueDate"
                        type="date"
                        autoComplete="off"
                        data-lpignore="true"
                        {...register("invoiceDueDate")}
                    />

                    <ErrorMessage size="sm" data-testid="invoiceDueDateError">
                        {invoiceDueDateError}
                    </ErrorMessage>
                </Field.Root>
            </Flex>
            <Field.Root invalid={Boolean(invoiceNumberError)} maxW={{ base: "100%", md: "20rem" }}>
                <Flex gap="0.5rem" alignItems="center">
                    <Field.Label fontWeight="anchor.bold">
                        {t("invoices:createInvoice.feesAndExpenses.invoiceReference")}
                    </Field.Label>
                    <PreFilledBadges fieldName="invoiceNumber" />
                </Flex>

                <Input
                    data-testid="invoiceNumber"
                    id="invoiceNumber"
                    {...register("invoiceNumber")}
                    data-lpignore="true"
                />

                <ErrorMessage size="sm" data-testid="invoiceNumberError">
                    {invoiceNumberError}
                </ErrorMessage>
            </Field.Root>
        </Box>
    );
};
